/**
 * EntitiesList - Generic List Component
 *
 * A reusable component that renders standardized list views for all entity types
 * (Projects, Deals, Valuations, Automations) using configuration-driven approach.
 *
 * Features:
 * - Table and card view layouts
 * - Integrated header with metrics and actions
 * - Search and filter functionality
 * - Loading and error states
 * - Status indicators and visual cues
 * - Empty state handling
 * - Entity-specific customization through props
 */

import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Components
import LoadingSpinner from "../ui/LoadingSpinner";
import ErrorDisplay from "../ui/ErrorDisplay";
import ComponentErrorBoundary from "../ErrorBoundary";
import SearchAndFilterBar from "../ui/SearchAndFilterBar";
import { StatusBadge } from "../ui/StatusIndicators";

// Hooks
import useSearchAndFilter from "../../hooks/useSearchAndFilter";

const EntitiesList = ({
  // Entity configuration
  entityConfig,
  entityColumns,
  entityMetrics,

  // Data and state
  entities = [],
  loading = false,
  error = null,

  // API hooks (optional)
  refreshFunction = null,
  clearErrorFunction = null,

  // Layout preferences
  layout = "table", // 'table' | 'cards'

  // Additional components
  HeaderActions = null,
  EmptyStateActions = null,

  // Custom className
  className = "",
}) => {
  // Configuration validation
  if (!entityConfig || !entityColumns) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">
          EntitiesList: Missing required configuration (entityConfig,
          entityColumns)
        </p>
      </div>
    );
  }

  const {
    entity,
    entitySingular,
    entityPlural,
    searchFields,
    filterFields,
    statusOptions,
    emptyState,
    features = {},
  } = entityConfig;

  // Search and filter setup
  const searchAndFilterProps = useSearchAndFilter({
    data: entities,
    searchFields: searchFields || ["name"],
    filterFields: filterFields || ["status"],
  });

  const {
    data: filteredEntities = [],
    searchTerm: searchValue = "",
    filters = {},
    isFiltering = false,
    handleSearchChange: onSearchChange = () => {},
    handleFiltersChange: onFiltersChange = () => {},
    filteredCount = 0,
    totalCount = 0,
  } = searchAndFilterProps || {};

  // Create custom onRemoveFilter function for individual filter removal
  const onRemoveFilter = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.filter((v) => v !== value);

    const newFilters = { ...filters };
    if (newValues.length === 0) {
      delete newFilters[filterType];
    } else {
      newFilters[filterType] = newValues;
    }

    onFiltersChange(newFilters);
  };

  // Dynamic filter options based on actual data
  const dynamicFilterOptions = useMemo(() => {
    const options = {};

    if (filterFields?.includes("status") && statusOptions) {
      options.status = statusOptions;
    }

    // Add other filter options based on filterFields
    filterFields?.forEach((field) => {
      if (field !== "status" && entities && entities.length > 0) {
        const uniqueValues = [
          ...new Set(entities.map((entity) => entity[field]).filter(Boolean)),
        ];

        options[field] = uniqueValues.map((value) => ({
          value,
          label:
            typeof value === "string"
              ? value.charAt(0).toUpperCase() + value.slice(1).replace("-", " ")
              : value,
        }));
      }
    });

    return options;
  }, [entities, filterFields, statusOptions]);

  // Calculate metrics using entityMetrics if provided
  const metrics = useMemo(() => {
    if (!entityMetrics?.calculateMetrics) {
      return { total: entities?.length || 0 };
    }
    return entityMetrics.calculateMetrics(entities || []);
  }, [entities, entityMetrics]);

  // Check if we have active search or filters
  const hasActiveSearchOrFilters = Boolean(
    searchValue || Object.keys(filters).length > 0
  );

  // Refresh function
  const refresh = () => {
    if (refreshFunction) {
      refreshFunction();
    }
  };

  // Clear error function
  const clearError = () => {
    if (clearErrorFunction) {
      clearErrorFunction();
    }
  };

  // Loading state with no data
  if (loading && (!entities || entities.length === 0)) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <LoadingSpinner size="small" />
            <h1 className="text-3xl font-bold text-gray-900">{entityPlural}</h1>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-12">
          <div className="text-center">
            <LoadingSpinner size="large" />
            <p className="mt-4 text-gray-600">
              Loading {entityPlural.toLowerCase()}...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state with no data
  if (error && (!entities || entities.length === 0)) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
            <h1 className="text-3xl font-bold text-gray-900">{entityPlural}</h1>
          </div>
        </div>
        <ErrorDisplay
          error={error}
          onRetry={refresh}
          onDismiss={clearError}
          className="bg-white rounded-lg border border-gray-200"
        />
      </div>
    );
  }

  return (
    <ComponentErrorBoundary componentName={`${entitySingular}List`}>
      <div className={`space-y-6 ${className}`}>
        {/* Header with Status Cards */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Left side: Title and Description */}
            <div className="flex-shrink-0">
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">
                  {entityPlural}
                </h1>
                {loading && <LoadingSpinner size="small" />}
                {error && (
                  <ExclamationTriangleIcon
                    className="h-6 w-6 text-red-500"
                    title="Error loading data"
                  />
                )}
              </div>
              <p className="text-gray-600 mt-1">
                {entityConfig.description ||
                  `Manage your ${entityPlural.toLowerCase()}`}
                {hasActiveSearchOrFilters
                  ? ` (${filteredCount} of ${totalCount} shown)`
                  : ` (${entities?.length || 0} total)`}
              </p>
            </div>

            {/* Center: Status Cards */}
            {entityMetrics?.getStatusSummary && entities && (
              <div className="flex flex-col sm:flex-row gap-4 lg:gap-6">
                {entityMetrics.getStatusSummary(entities).map((stat, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 bg-gray-50 rounded-lg px-4 py-3"
                  >
                    <div
                      className={`w-10 h-10 bg-${stat.color}-100 rounded-full flex items-center justify-center`}
                    >
                      <span
                        className={`text-${stat.color}-600 font-bold text-lg`}
                      >
                        {typeof stat.value === "number"
                          ? stat.value
                          : stat.value.split(" ")[0]}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {stat.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {stat.percentage ? `${stat.percentage}%` : stat.type}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Right side: Action Buttons */}
            <div className="flex items-center space-x-3 lg:flex-shrink-0">
              {refreshFunction && (
                <button
                  onClick={refresh}
                  disabled={loading}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  title="Refresh data"
                >
                  <ArrowPathIcon
                    className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                  />
                </button>
              )}
              {HeaderActions && <HeaderActions />}
              {/* {entityConfig.routes?.new && (
                <Link
                  to={entityConfig.routes.new}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Create {entitySingular}
                </Link>
              )} */}
            </div>
          </div>
        </div>

        {/* Error Banner (when there's data) */}
        {error && entities && entities.length > 0 && (
          <ErrorDisplay
            error={error}
            onRetry={refresh}
            onDismiss={clearError}
            type="warning"
            className="mb-4"
          />
        )}

        {/* Search and Filter Bar */}
        <SearchAndFilterBar
          {...searchAndFilterProps}
          filterOptions={dynamicFilterOptions}
          placeholder={`Search ${entityPlural.toLowerCase()} by ${searchFields?.join(", ") || "name"}...`}
          className="bg-white p-4 rounded-lg border border-gray-200"
        />

        {/* Results Summary */}
        {hasActiveSearchOrFilters && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-wrap">
                {isFiltering ? (
                  <div className="flex items-center space-x-2 text-sm text-blue-800">
                    <LoadingSpinner size="small" />
                    <span>Filtering {entityPlural.toLowerCase()}...</span>
                  </div>
                ) : (
                  <span className="text-sm text-blue-800">
                    Showing {filteredCount} of {totalCount}{" "}
                    {entityPlural.toLowerCase()}
                    {filteredCount !== totalCount && (
                      <span className="font-medium"> (filtered)</span>
                    )}
                  </span>
                )}

                {/* Active Filter Chips */}
                {Object.entries(filters).map(([filterType, values]) =>
                  (values || []).map((value) => (
                    <div
                      key={`${filterType}-${value}`}
                      className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full border border-blue-200"
                    >
                      <span className="text-xs font-medium capitalize">
                        {filterType}:
                      </span>
                      <span>
                        {dynamicFilterOptions[filterType]?.find(
                          (opt) => opt.value === value
                        )?.label || value}
                      </span>
                      <button
                        onClick={() => onRemoveFilter(filterType, value)}
                        className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                        aria-label={`Remove ${filterType} filter: ${value}`}
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>
              <button
                onClick={() => onFiltersChange({})}
                className="text-sm text-blue-600 hover:text-blue-700 underline focus:outline-none"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {filteredEntities.length === 0 && !hasActiveSearchOrFilters ? (
          // Empty state - no entities at all
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <span className="text-6xl mb-4 block">
                  {emptyState?.icon || "📊"}
                </span>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {emptyState?.title || `No ${entityPlural} Yet`}
                </h2>
                <p className="text-gray-600 mb-6">
                  {emptyState?.description ||
                    `Get started by creating your first ${entitySingular.toLowerCase()}`}
                </p>
                <div className="flex justify-center">
                  {EmptyStateActions ? (
                    <EmptyStateActions />
                  ) : emptyState?.actionRoute ? (
                    <Link
                      to={emptyState.actionRoute}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      {emptyState.actionText || `Create ${entitySingular}`}
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : filteredEntities.length === 0 && hasActiveSearchOrFilters ? (
          // No results found with filters
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <span className="text-6xl mb-4 block">🔍</span>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  No {entityPlural} Found
                </h2>
                <p className="text-gray-600 mb-6">
                  No {entityPlural.toLowerCase()} match your current search
                  criteria or filters. Try adjusting your search terms or
                  clearing filters.
                </p>
                <button
                  onClick={() => {
                    onSearchChange("");
                    onFiltersChange({});
                  }}
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Data table
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">
                  {entitySingular} List
                </h2>
                <div className="text-sm text-gray-600">
                  {hasActiveSearchOrFilters ? (
                    <span>
                      {filteredCount} of {totalCount}{" "}
                      {entityPlural.toLowerCase()}
                      {isFiltering && (
                        <span className="ml-2">
                          <LoadingSpinner size="small" className="inline" />
                        </span>
                      )}
                    </span>
                  ) : (
                    <span>
                      {filteredEntities.length} {entityPlural.toLowerCase()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {(entityColumns || []).map((column) => (
                      <th
                        key={column.key}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        style={{ width: column.width }}
                      >
                        {column.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEntities.map((entity) => (
                    <tr
                      key={entity.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {(entityColumns || []).map((column) => {
                        const value = column.key.includes(".")
                          ? column.key
                              .split(".")
                              .reduce((obj, key) => obj?.[key], entity)
                          : entity[column.key];

                        return (
                          <td
                            key={column.key}
                            className="px-6 py-4 whitespace-nowrap"
                            style={{ width: column.width }}
                          >
                            {column.render
                              ? column.render(value, entity)
                              : value || "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </ComponentErrorBoundary>
  );
};

export default EntitiesList;
